import {
  ZfBox,
  ZfDivider,
  ZfFormField,
  ZfIcon,
  ZfIconButton,
  ZfInput,
  ZfLoadingSpinner,
  ZfSelectOption,
  ZfStack,
  ZfTextButton,
  ZfTypography,
} from '@zafire/react';
import { CashAccount, PortfolioForm } from './AddPortfolioDialog';
import { useFormikContext } from 'formik';
import { useGetDropdown } from './hooks/useGetDropdown';
import { MainCustodyLinkSection } from './AddPortfolioSections/MainCustodyLinkSection';
import { PortfolioConfigSection } from './AddPortfolioSections/PortfolioConfigSection';
import { PortfolioType, PortfolioTypeRule, ResponseError } from 'pco-product-service-internal-consumer';
import { List, RowComponentProps } from 'react-window';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { searchSuggestionFilterData } from '../../utils/searchSuggestionFilterUtils';
import useGetPortfolioBySA from './hooks/useGetPortfolioBySA';
import { useTranslation } from 'react-i18next';
import { PortfolioDataResponse } from '../../types/portfolios/portfolioData';
import { useGetContactNotes } from '../../hooks/queries/contactNotes';
import { LinkedContactNotesGrid } from '../../views/PrivateBanking/LinkedContactNotesGrid';
import { closeModal } from '../../reducers/modalReducer';
import { useGetServiceAgreementDetails, useUpdateServiceAgreement } from '../../hooks/queries/privateBanking';
import { clearSelectedPBProduct, selectSelectedPBProduct } from '../../reducers/privateBankingReducer';
import { ServicingRoleSection } from './AddPortfolioSections/ServicingRoleSection';
import { ConfirmCloseProductModal } from '../../views/PrivateBanking/modals/ConfirmCloseProductModal';
import { showToast } from '../../reducers/toastReducer';

export type RowProps = RowComponentProps & {
  item: CashAccount;
};

type PortfolioDetailSectionType = {
  brUid: string;
  serviceAgreementId?: string;
  isLoading: boolean;
  isEdit: boolean;
  portfolio?: PortfolioDataResponse;
  isStandaloneCreate?: boolean;
  portfolioRules?: readonly PortfolioTypeRule[];
  onCloseServiceAgreementError?: (message: string, error?: Error) => void;
};

const DROPDOWN_HEIGHT = 32;

const PortfolioDetailSection = ({
  brUid,
  serviceAgreementId,
  isLoading = false,
  isEdit,
  portfolio,
  isStandaloneCreate = false,
  portfolioRules,
  onCloseServiceAgreementError,
}: PortfolioDetailSectionType) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { values, touched, errors, setFieldValue } = useFormikContext<PortfolioForm>();
  const showCashAccounts =
    !!serviceAgreementId || (isStandaloneCreate && values.portfolioType === PortfolioType.SlbRepo);
  const { currencyData, cashAccountTypeData } = useGetDropdown();
  const [cashAccountSuggestion, setCashAccountSuggestion] = useState('');
  const [currencySuggestion, setCurrencySuggestion] = useState('');
  const [isConfirmingCloseServiceAgreement, setIsConfirmingCloseServiceAgreement] = useState(false);
  const selectedPBProduct = useSelector(selectSelectedPBProduct);

  const { data: serviceAgreementData } = useGetServiceAgreementDetails(serviceAgreementId);
  const { mutateAsync: closeServiceAgreement, isPending: isClosingServiceAgreement } = useUpdateServiceAgreement();

  const handleOpenCloseServiceAgreementConfirm = () => {
    setIsConfirmingCloseServiceAgreement(true);
  };

  const handleCancelCloseServiceAgreement = () => {
    setIsConfirmingCloseServiceAgreement(false);
  };

  const handleCloseServiceAgreement = async () => {
    if (!serviceAgreementId || !serviceAgreementData) {
      return;
    }
    try {
      await closeServiceAgreement({ serviceAgreement: serviceAgreementData, brUid });
      if (selectedPBProduct) {
        dispatch(clearSelectedPBProduct());
      }
      dispatch(
        showToast({
          type: 'success',
          position: 'top-center',
          titleText: t('pb.portfolio.closed.toast.title'),
          dismissible: true,
          autoDismissDuration: 5000,
        }),
      );
      dispatch(closeModal());
    } catch (e: unknown) {
      let message = t('pb.portfolio.error.unknown');
      let errorObject: Error | undefined;

      if (e instanceof ResponseError) {
        try {
          const errorBody = await e.response.json();
          message = errorBody.title ?? errorBody.message ?? message;
          errorObject = errorBody;
        } catch {
          message = await e.response.text();
          errorObject = e instanceof Error ? e : new Error(String(e));
        }
      } else if (e instanceof Error) {
        message = e.message;
        errorObject = e;
      }

      onCloseServiceAgreementError?.(message, errorObject);
      setIsConfirmingCloseServiceAgreement(false);
    }
  };

  const cashAccountFilteredData = searchSuggestionFilterData({
    data: cashAccountTypeData,
    searchTerm: cashAccountSuggestion,
  });
  const currencyFilteredData = searchSuggestionFilterData({ data: currencyData, searchTerm: currencySuggestion });

  const { data: portfolioData } = useGetPortfolioBySA(serviceAgreementId || '', !!serviceAgreementId);

  const { data: linkedContactNotes = [] } = useGetContactNotes(serviceAgreementId);

  const cashAccountNumberToDisabled = useMemo(() => {
    if (portfolioData?.cashAccounts) {
      return portfolioData.cashAccounts.length;
    }
    return 0;
  }, [portfolioData?.cashAccounts]);

  const Row = useCallback(
    ({ index, style, item }: RowProps) => {
      const co = cashAccountFilteredData?.[index];
      if (!co) return null;

      return (
        <ZfSelectOption style={style} value={co.value.code} isSelected={item.accountType === co.value.value}>
          {co.value.label}
        </ZfSelectOption>
      );
    },
    [cashAccountFilteredData],
  );

  return (
    <ZfStack spacing="medium">
      <ZfStack direction="row" spacing="small">
        <ZfTypography variant="label-medium" color="emphasis" weight="bold">
          {t('pb.portfoliodetail.addportfolio.title')}{' '}
        </ZfTypography>
        <ZfDivider style={{ flex: 1, paddingTop: 'var(--padding-top-xs)' }} />
      </ZfStack>
      <PortfolioConfigSection
        brUid={brUid}
        isLoading={isLoading}
        isStandaloneCreate={isStandaloneCreate}
        isEdit={isEdit}
        existingPortfolio={portfolio}
        portfolioRules={portfolioRules}
      />
      {showCashAccounts && (
        <>
          <ZfStack direction="row" spacing="small">
            <ZfTypography variant="label-medium" color="emphasis" weight="bold">
              {t('pb.portfoliodetail.addcashaccounts.title')}
            </ZfTypography>
            <ZfDivider style={{ flex: 1, paddingTop: 'var(--padding-top-xs)' }} />
          </ZfStack>
          {values.cashAccounts.length === 0 && errors.cashAccounts && touched.cashAccounts && (
            <ZfTypography variant="label-small" color="danger">
              {t('pb.portfoliodetail.cashaccounts.minrequired')}
            </ZfTypography>
          )}
          <ZfStack spacing="small">
            {values.cashAccounts.map((item, index) => {
              return (
                <ZfStack direction="row" spacing="small" key={`cashAccount_${index}`} styles={{ alignItems: 'center' }}>
                  <ZfFormField
                    style={{ flex: 1 }}
                    label={t('pb.cashaccount.accounttype.label')}
                    alerts={[
                      typeof errors?.cashAccounts?.[index] === 'object'
                        ? (errors.cashAccounts[index]?.accountType ?? '')
                        : '',
                    ]}
                    mandatory
                  >
                    <ZfInput
                      name={`cashAccounts-${index}-accountType`}
                      type="select"
                      onSelectChange={(e) => setFieldValue(`cashAccounts.${index}.accountType`, e.detail)}
                      allowSearch
                      readonly={index < cashAccountNumberToDisabled}
                      error={
                        typeof errors?.cashAccounts?.[index] === 'object' &&
                        !!errors.cashAccounts[index]?.accountType &&
                        typeof touched?.cashAccounts?.[index] === 'object' &&
                        !!touched.cashAccounts[index]?.accountType
                      }
                      id={`cashAccounts-${index}-accountType`}
                      value={item.accountType}
                      onInputDropdownOpen={() => setCashAccountSuggestion('')}
                      onInputChange={(e) => setCashAccountSuggestion(e.detail)}
                      getOptionLabel={(option) =>
                        cashAccountTypeData?.find((co) => co.value.code === option)?.value?.label || ''
                      }
                    >
                      {isLoading && (
                        <div slot="trailing" aria-live="polite">
                          <ZfLoadingSpinner
                            size="extra-small"
                            color="primary"
                            type="infinite"
                            aria-label="Loading"
                          ></ZfLoadingSpinner>
                        </div>
                      )}
                      {cashAccountSuggestion !== '' && cashAccountFilteredData?.length === 0 && (
                        <ZfBox
                          style={{
                            padding: 'var(--gap-vertical-xs)',
                            paddingBottom: 'var(--gap-vertical-xs)',
                            paddingLeft: 'var(--gap-horizontal-md)',
                          }}
                        >
                          <ZfTypography variant="body-small" color="normal">
                            {t('pb.cashaccount.empty.description')}
                          </ZfTypography>
                        </ZfBox>
                      )}
                      {cashAccountFilteredData && (
                        <List
                          rowCount={cashAccountFilteredData.length}
                          rowHeight={DROPDOWN_HEIGHT}
                          overscanCount={5}
                          style={{
                            height: Math.min(cashAccountFilteredData.length, 10) * DROPDOWN_HEIGHT,
                            width: '100%',
                          }}
                          rowProps={{
                            item: item,
                          }}
                          rowComponent={Row}
                        ></List>
                      )}
                    </ZfInput>
                  </ZfFormField>
                  <ZfFormField
                    style={{ flex: 1 }}
                    label={t('pb.cashaccount.currency.label')}
                    alerts={[
                      typeof errors?.cashAccounts?.[index] === 'object'
                        ? (errors.cashAccounts[index]?.currency ?? '')
                        : '',
                    ]}
                    mandatory
                  >
                    <ZfInput
                      name={`cashAccounts-${index}-currency`}
                      type="select"
                      onSelectChange={(e) => {
                        setFieldValue(`cashAccounts.${index}.currency`, e.detail);
                      }}
                      allowSearch
                      error={
                        typeof errors?.cashAccounts?.[index] === 'object' &&
                        !!errors.cashAccounts[index]?.currency &&
                        typeof touched?.cashAccounts?.[index] === 'object' &&
                        !!touched.cashAccounts[index]?.currency
                      }
                      id={`cashAccounts-${index}-currency`}
                      value={item.currency}
                      onInputDropdownOpen={() => setCurrencySuggestion('')}
                      onInputChange={(e) => setCurrencySuggestion(e.detail)}
                      getOptionLabel={(option) =>
                        currencyData?.find((co) => co.value.code === option)?.value?.label || option
                      }
                      readonly={index < cashAccountNumberToDisabled}
                    >
                      {isLoading && (
                        <div slot="trailing" aria-live="polite">
                          <ZfLoadingSpinner
                            size="extra-small"
                            color="primary"
                            type="infinite"
                            aria-label="Loading"
                          ></ZfLoadingSpinner>
                        </div>
                      )}
                      {currencySuggestion !== '' && currencyFilteredData?.length === 0 && (
                        <ZfBox
                          style={{
                            padding: 'var(--gap-vertical-xs)',
                            paddingBottom: 'var(--gap-vertical-xs)',
                            paddingLeft: 'var(--gap-horizontal-md)',
                          }}
                        >
                          <ZfTypography variant="body-small" color="normal">
                            {t('pb.cashaccount.currency.empty.description')}
                          </ZfTypography>
                        </ZfBox>
                      )}
                      {currencyFilteredData?.map((co) => {
                        return (
                          <ZfSelectOption
                            key={co.key}
                            value={co.value.value}
                            isSelected={item.currency === co.value.value}
                          >
                            {co.value.label}
                          </ZfSelectOption>
                        );
                      })}
                    </ZfInput>
                  </ZfFormField>

                  <ZfIconButton
                    size="large"
                    hierarchy="tertiary"
                    disabled={index < cashAccountNumberToDisabled || index === 0}
                    style={{ paddingTop: 20 }}
                    onClick={() => {
                      const newList = values.cashAccounts.filter((_, i) => i !== index);
                      setFieldValue('cashAccounts', newList);
                    }}
                  >
                    <ZfIcon name="delete" />
                  </ZfIconButton>
                </ZfStack>
              );
            })}
          </ZfStack>
          <ZfStack direction="row" justify="flex-end">
            <ZfTextButton
              style={{ maxWidth: '200px' }}
              onClick={() => {
                setFieldValue('cashAccounts', [...values.cashAccounts, { currency: '', accountType: '' }]);
              }}
              hierarchy="secondary"
              size="medium"
            >
              <ZfIcon name="add_circle" slot="leading" size="medium"></ZfIcon>
              {t('pb.cashaccount.addbutton')}
            </ZfTextButton>
          </ZfStack>
        </>
      )}
      {!portfolio && !isStandaloneCreate && (
        <>
          <ZfStack direction="row" spacing="small">
            <ZfTypography variant="label-medium" color="emphasis" weight="bold">
              {t('pb.portfoliodetail.linkcontactnotes.title')}
            </ZfTypography>
            <ZfDivider style={{ flex: 1, paddingTop: 'var(--padding-top-xs)' }} />
          </ZfStack>
          <LinkedContactNotesGrid contactNotes={linkedContactNotes} showActions={false} />
        </>
      )}
      {!portfolio && serviceAgreementData?.canBeClosed && (
        <>
          <ZfStack direction="row" spacing="small">
            <ZfTypography variant="label-medium" color="emphasis" weight="bold">
              {t('pb.deleteproductmodal.title')}
            </ZfTypography>
            <ZfDivider style={{ flex: 1, paddingTop: 'var(--padding-top-xs)' }} />
          </ZfStack>
          <ZfStack>
            <ZfTextButton
              kind="destructive"
              hierarchy="secondary"
              size="medium"
              style={{ maxWidth: '180px' }}
              disabled={!serviceAgreementId || isClosingServiceAgreement}
              onClick={handleOpenCloseServiceAgreementConfirm}
            >
              {isClosingServiceAgreement ? (
                <ZfLoadingSpinner size="small" slot="leading" />
              ) : (
                <ZfIcon name="delete" slot="leading" size="medium"></ZfIcon>
              )}
              {t('pb.deleteproductmodal.title')}
            </ZfTextButton>
          </ZfStack>
          {isConfirmingCloseServiceAgreement && (
            <ConfirmCloseProductModal
              title={t('pb.deleteproductmodal.title', 'Close product')}
              bodyText={t('pb.investmentproduct.closeconfirm.body', {
                productName: serviceAgreementData?.serviceModel?.productDisplayName,
              })}
              confirmLabel={t('pb.deleteproductmodal.submit', 'Close product')}
              cancelLabel={t('pb.deleteproductmodal.cancel', 'Cancel')}
              isPending={isClosingServiceAgreement}
              onConfirm={handleCloseServiceAgreement}
              onCancel={handleCancelCloseServiceAgreement}
            />
          )}
        </>
      )}
      <ServicingRoleSection isStandaloneCreate={isStandaloneCreate} portfolioRules={portfolioRules} />
      <MainCustodyLinkSection brUid={brUid} isStandaloneCreate={isStandaloneCreate} portfolioRules={portfolioRules} />
    </ZfStack>
  );
};

export default PortfolioDetailSection;
